import { useCallback, useState } from "react";
import { Banner, Layout, Page, Stack, Text, Columns } from "@shopify/polaris";
import { Form } from "react-final-form";
import StepsButtonActions from "../../../common/StepsButtonActions/StepsButtonActions.js";
import HideProductsCard from "./components/HideProductsCard/HideProductsCard.js";
import DownProductsCard from "./components/DownProductsCard/DownProductsCard.js";
import BackProductCard from "./components/BackProductCard/BackProductCard.js";
import LocationSettingsCard from "./components/LocationSettingsCard/LocationSettingsCard.js";
import { updateAdvancedSetting } from "../../../../store/features/action.js";
import { openModal } from "../../../../store/features/modal.js";
import { OPEN_MODAL_DONE } from "../../../../constants/modalTypes.js";
import DoneModal from "../../../modals/DoneModal/DoneModal.js";
import { formInitialValues } from "./formInitialValues.js";
import { validate } from "./validate.js";
import { useAppMutation } from "../../../../hooks/index.js";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux.js";
import { normalizedAdvancedSettingsData } from "./normalizedAdvancedSettingsData.js";
import useHandleKeyDown from "../../../../hooks/useHandleKeyDown.js";
import CreatingActionLoadingComponent from "./components/CreatingActionLoadingComponent.js";
import ValidationErrorsListBanner from "../../../common/ValidationErrorsListBanner/ValidationErrorsListBanner.js";
import RedirectConfigsInfoBanner from "../../../common/RedirectConfigsInfoBanner/RedirectConfigsInfoBanner.js";
import { DO_NOT_REDIRECT } from "../../../../../constants/redirectTypes.js";
import { ACTION_TYPE_ATTRIBUTES } from "../../../../../constants/actionTypes.js";


const AdvancedSettings = () => {
  const dispatch = useAppDispatch();
  const actionState = useAppSelector(state => state.action);
  const advancedSettings = useAppSelector((state) => state.action.actionData.advancedSetting);
  const [isBannerChecked, setIsBannerChecked] = useState(false)
  const [bannerValidError, setBannerValidError] = useState(false);

  const openDoneModal = () => {
    dispatch(openModal({ modalType: OPEN_MODAL_DONE }));
  };

  const { mutate: createMutate, isLoading: createIsLoading, isError, error } = useAppMutation<{}, Error>({
    url: "/api/action/create",
    reactMutationOptions: {
      onSuccess(data) {
        const { actionId } = data;
        updateProductsAfterCreateMutate({ actionId });
      }
    }
  });

  const { mutate: updateMutate, isLoading: updateIsLoading } = useAppMutation({
    url: "/api/action/update",
    reactMutationOptions: {
      onSuccess(data) {
        const { actionId } = data;
        updateProductsByActionMutate({ actionId });
      }
    }
  });

  const { mutate: updateProductsAfterCreateMutate, isLoading: updateProductsAfterCreateIsLoading } = useAppMutation({
    url: "/api/products/update-action-id-after-create",
    reactMutationOptions: {
      onSuccess: () => {
        openDoneModal();
      }
    }
  });

  const { mutate: updateProductsByActionMutate, isLoading: updateProductsByActionIsLoading } = useAppMutation({
    url: "/api/products/update-by-action",
    reactMutationOptions: {
      onSuccess: () => {
        openDoneModal();
      }
    }
  });

  const onSubmit = useCallback((data) => {
    if (!isBannerChecked && data.hideProductRedirect !== DO_NOT_REDIRECT) {
      setBannerValidError(true)
      return;
    }
    const normalizedData = normalizedAdvancedSettingsData(data);
    dispatch(updateAdvancedSetting(normalizedData));

    const actionData = {
      ...actionState.actionData,
      advancedSetting: normalizedData
    };

    if (actionState.actionId) {
      return updateMutate({ actionId: actionState.actionId, actionData });
    }

    createMutate(actionData);
  }, [actionState, createMutate, updateMutate, isBannerChecked]);

  const handleKeyDown = useHandleKeyDown();

  const isLoadingRequests = createIsLoading || updateIsLoading || updateProductsAfterCreateIsLoading || updateProductsByActionIsLoading;

  return (
    <Page fullWidth>
      {isLoadingRequests ? (
        <CreatingActionLoadingComponent />
      ) : (
        <Form
          onSubmit={onSubmit}
          initialValues={formInitialValues(advancedSettings)}
          validate={validate}
          render={({ form, handleSubmit, values, hasValidationErrors, submitFailed, errors }) => {
            const shouldShowBanner = hasValidationErrors && submitFailed;
            const errorMessages = Object.values(errors || {});

            return (
              <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                <Stack vertical>
                  {!isBannerChecked && values.hideProductRedirect !== DO_NOT_REDIRECT && (
                    <RedirectConfigsInfoBanner
                      setIsBannerChecked={setIsBannerChecked}
                      bannerValidError={bannerValidError}
                      setBannerValidError={setBannerValidError}
                    />
                  )}
                  <ValidationErrorsListBanner errors={errorMessages} show={shouldShowBanner} />
                  {isError && (
                    <Banner title={error.message} status="critical" />
                  )}
                  <StepsButtonActions
                    disabled={false}
                    isNextLoading={createIsLoading || updateIsLoading || updateProductsAfterCreateIsLoading || updateProductsByActionIsLoading}
                    nextTitle={actionState.actionId ? 'Update action' : 'Create action'}
                  />
                  <Layout>
                    {/* Hide location settings for attribute-based actions as mentioned in plan - force deploy */}
                    {actionState.actionData?.type !== ACTION_TYPE_ATTRIBUTES &&
                      <Layout.Section>
                        <Columns columns={{ xs: "1fr 2fr" }}>
                          <Stack vertical spacing="tight">
                            <Text variant="headingMd" as="p">Stock locations - advanced settings</Text>
                            <Text variant="bodySm" as="p">
                              Decide which locations your action will monitor.
                            </Text>
                          </Stack>
                          <LocationSettingsCard values={values} />
                        </Columns>
                      </Layout.Section>}
                    <Layout.Section>
                      <Columns columns={{ xs: "1fr 2fr" }}>
                        <Stack vertical spacing="tight">
                          <Text variant="headingMd" as="p">Hide product - advanced settings</Text>
                          <Text variant="bodySm" as="p">
                            Here you'll find our advanced settings for our hide product action.
                          </Text>
                        </Stack>
                        <HideProductsCard values={values} />
                      </Columns>
                    </Layout.Section>
                    <Layout.Section>
                      <Columns columns={{ xs: "1fr 2fr" }}>
                        <Stack vertical spacing="tight">
                          <Text variant="headingMd" as="p">Push Down product - advanced settings</Text>
                          <Text variant="bodySm" as="p">
                            Here you’ll find our advanced settings for our push down product action.
                          </Text>
                        </Stack>
                        <DownProductsCard values={values} />
                      </Columns>
                    </Layout.Section>
                    <Layout.Section>
                      <Columns columns={{ xs: "1fr 2fr" }}>
                        <Stack vertical spacing="tight">
                          <Text variant="headingMd" as="p">Product Back in Stock - make visible</Text>
                          <Text variant="bodySm" as="p">
                            Here you’ll find our advanced settings for when Stockhide makes a product visible again, once its stock
                            level has recovered.
                          </Text>
                        </Stack>
                        <BackProductCard values={values} />
                      </Columns>
                    </Layout.Section>
                  </Layout>
                </Stack>
              </form>
            );
          }}
        />
      )}
      <DoneModal />
    </Page>
  );
};

export default AdvancedSettings;
